import React from 'react';
import { Link } from 'react-router-dom';
import { Facebook, Instagram, Linkedin, Youtube, Mail, Phone, MapPin, MessageCircle } from 'lucide-react';
import { useContactInfo, getWhatsAppHref, getTelHref } from '../contexts/ContactInfoContext';
import { TREATMENT_SERVICES, PATIENT_SUPPORT, FOR_CLINICS } from '../data/navigation';
import { COMPANY_DETAILS, COMPANY_STATEMENT } from '../data/company';

// TikTok inline SVG (lucide doesn't ship a TikTok icon)
const TikTokIcon = ({ className = 'h-5 w-5' }) => (
  <svg viewBox="0 0 24 24" fill="currentColor" className={className} aria-hidden="true">
    <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5.8 20.1a6.34 6.34 0 0 0 10.86-4.43V9.83a8.16 8.16 0 0 0 4.77 1.52V7.91a4.85 4.85 0 0 1-1.84-1.22z" />
  </svg>
);

const Footer = () => {
  const { contactInfo } = useContactInfo();

  const whatsappHref = getWhatsAppHref(contactInfo, 'Hi VietClinics');
  const telHref = getTelHref(contactInfo);

  const handleWhatsApp = () => {
    if (!whatsappHref) return;
    window.open(whatsappHref, '_blank');
  };

  const socials = [
    { url: contactInfo?.facebookUrl,  Icon: Facebook,   label: 'Facebook',  testid: 'footer-social-facebook' },
    { url: contactInfo?.instagramUrl, Icon: Instagram,  label: 'Instagram', testid: 'footer-social-instagram' },
    { url: contactInfo?.linkedinUrl,  Icon: Linkedin,   label: 'LinkedIn',  testid: 'footer-social-linkedin' },
    { url: contactInfo?.youtubeUrl,   Icon: Youtube,    label: 'YouTube',   testid: 'footer-social-youtube' },
    { url: contactInfo?.tiktokUrl,    Icon: TikTokIcon, label: 'TikTok',    testid: 'footer-social-tiktok' },
  ].filter((s) => s.url && s.url.trim());

  return (
    <footer className="bg-gray-900 text-gray-300">
      <div className="container mx-auto px-4 py-12">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-8 gap-8">
          {/* Company Info */}
          <div className="lg:col-span-2">
            <div className="flex items-center space-x-2 mb-4">
              <div className="flex items-center justify-center w-10 h-10 rounded-lg bg-gradient-to-br from-[#1FA7A1] to-[#1E88E5]">
                <span className="text-white font-bold text-xl">V</span>
              </div>
              <span className="text-xl font-bold text-white">VietClinics</span>
            </div>
            <p className="text-sm mb-4 leading-relaxed">
              Vietnam's independent healthcare coordination service — helping patients and travellers explore care and coordinate with selected providers across Vietnam.
            </p>
            {socials.length > 0 && (
              <div className="flex space-x-4 mb-4">
                {socials.map(({ url, Icon, label, testid }) => (
                  <a
                    key={label}
                    href={url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="hover:text-[#1FA7A1] transition-colors"
                    aria-label={label}
                    data-testid={testid}
                  >
                    <Icon className="h-5 w-5" />
                  </a>
                ))}
              </div>
            )}
          </div>

          {/* Company */}
          <div>
            <h3 className="text-white font-semibold mb-4">Company</h3>
            <ul className="space-y-2.5">
              <li><Link to="/about" className="text-sm hover:text-[#1FA7A1] transition-colors">About Us</Link></li>
              <li><Link to="/company-details" className="text-sm hover:text-[#1FA7A1] transition-colors" data-testid="footer-company-details-nav">Company Details</Link></li>
              <li><Link to="/how-it-works" className="text-sm hover:text-[#1FA7A1] transition-colors">How It Works</Link></li>
              <li><Link to="/pricing" className="text-sm hover:text-[#1FA7A1] transition-colors">For Clinics</Link></li>
              <li><Link to="/contact" className="text-sm hover:text-[#1FA7A1] transition-colors">Contact Us</Link></li>
            </ul>
          </div>

          {/* Treatments & Services */}
          <div data-testid="footer-treatments-services">
            <h3 className="text-white font-semibold mb-4">Treatments &amp; Services</h3>
            <ul className="space-y-2.5">
              {TREATMENT_SERVICES.map((s) => (
                <li key={s.to}>
                  <Link to={s.to} className="text-sm hover:text-[#1FA7A1] transition-colors" data-testid={`footer-service-${s.to.replace('/', '')}`}>{s.label}</Link>
                  {s.children && (
                    <div className="flex flex-wrap gap-x-2 gap-y-1 mt-1.5">
                      {s.children.map((c) => (
                        <Link key={c.to} to={c.to} className="text-xs text-gray-400 hover:text-[#1FA7A1] transition-colors" data-testid={`footer-checkup-${c.label.toLowerCase()}`}>{c.label}</Link>
                      ))}
                    </div>
                  )}
                </li>
              ))}
              <li><Link to="/treatments" className="text-sm hover:text-[#1FA7A1] transition-colors">Explore Treatments</Link></li>
            </ul>
          </div>

          {/* Patient Support */}
          <div data-testid="footer-patient-support-col">
            <h3 className="text-white font-semibold mb-4">Patient Support</h3>
            <ul className="space-y-2.5">
              {PATIENT_SUPPORT.map((s) => (
                <li key={s.to}><Link to={s.to} className="text-sm hover:text-[#1FA7A1] transition-colors" data-testid={`footer-support-${s.to.split('/').pop()}`}>{s.label}</Link></li>
              ))}
              <li><Link to="/patient-support-vietnam" className="text-sm hover:text-[#1FA7A1] transition-colors" data-testid="footer-patient-support">Patient Support in Vietnam</Link></li>
              <li><Link to="/how-to-choose-doctor-vietnam" className="text-sm hover:text-[#1FA7A1] transition-colors" data-testid="footer-how-to-choose-doctor">How to Choose a Doctor</Link></li>
              <li><Link to="/#request-appointment" className="text-sm hover:text-[#1FA7A1] transition-colors">Request Appointment</Link></li>
              <li><Link to="/travel-hub" className="text-sm hover:text-[#1FA7A1] transition-colors">Travel Hub</Link></li>
              <li><Link to="/login" className="text-sm hover:text-[#1FA7A1] transition-colors">My Account</Link></li>
              <li><Link to="/faq" className="text-sm hover:text-[#1FA7A1] transition-colors">FAQ</Link></li>
            </ul>
          </div>

          {/* Urgent Care */}
          <div>
            <h3 className="text-white font-semibold mb-4">Urgent Care</h3>
            <ul className="space-y-2.5">
              <li>
                <Link
                  to="/urgent-medical-care"
                  className="text-sm hover:text-[#1FA7A1] transition-colors"
                  data-testid="footer-urgent-pillar"
                >
                  Urgent Medical Care in Vietnam
                </Link>
              </li>
              <li>
                <Link
                  to="/doctor-to-hotel-vietnam"
                  className="text-sm hover:text-[#1FA7A1] transition-colors"
                  data-testid="footer-urgent-d2h"
                >
                  Doctor-to-Hotel in Vietnam
                </Link>
              </li>
              <li>
                <Link
                  to="/english-speaking-doctor-vietnam"
                  className="text-sm hover:text-[#1FA7A1] transition-colors"
                  data-testid="footer-urgent-esd"
                >
                  English-Speaking Doctors
                </Link>
              </li>
              <li>
                <Link
                  to="/urgent-medical-care-hoi-an"
                  className="text-sm hover:text-[#1FA7A1] transition-colors"
                  data-testid="footer-urgent-hoian"
                >
                  Da Nang &amp; Hoi An
                </Link>
              </li>
              <li>
                <Link
                  to="/emergency"
                  className="text-sm hover:text-[#1FA7A1] transition-colors"
                  data-testid="footer-urgent-emergency"
                >
                  Emergency Hotlines
                </Link>
              </li>
            </ul>
            <p className="text-[11px] text-gray-500 mt-3 leading-snug" data-testid="footer-urgent-disclaimer">
              VietClinics coordinates non-emergency care with verified local
              providers. For life-threatening emergencies, dial <strong className="text-gray-300">115</strong>.
            </p>
          </div>

          {/* For Clinics */}
          <div data-testid="footer-for-clinics-col">
            <h3 className="text-white font-semibold mb-4">For Clinics</h3>
            <ul className="space-y-2.5">
              {FOR_CLINICS.map((s) => (
                <li key={s.to}><Link to={s.to} className="text-sm hover:text-[#1FA7A1] transition-colors" data-testid={`footer-clinics-${s.label.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}>{s.label}</Link></li>
              ))}
            </ul>
          </div>

          {/* Legal & Support */}
          <div>
            <h3 className="text-white font-semibold mb-4">Legal</h3>
            <ul className="space-y-2.5">
              <li><Link to="/terms" className="text-sm hover:text-[#1FA7A1] transition-colors">Terms & Conditions</Link></li>
              <li><Link to="/privacy" className="text-sm hover:text-[#1FA7A1] transition-colors">Privacy Policy</Link></li>
              <li><Link to="/payment-policy" className="text-sm hover:text-[#1FA7A1] transition-colors">Refund Policy</Link></li>
              <li><Link to="/contact" className="text-sm hover:text-[#1FA7A1] transition-colors">Support</Link></li>
            </ul>
          </div>
        </div>

        {/* ── Explore Our Services in Vietnam ───────────────────────────── */}
        <div className="border-t border-gray-800 mt-8 pt-8 mb-8">
          <h3 className="text-sm font-semibold text-white mb-4" data-testid="footer-popular-heading">
            Explore Our Services in Vietnam
          </h3>
          <div className="flex flex-wrap gap-2">
            {[
              { label: 'Dental Clinics in Vietnam',        to: '/dental-clinics-vietnam' },
              { label: 'Hair Transplant in Vietnam',       to: '/hair-transplant-vietnam' },
              { label: 'Cosmetic Surgery in Vietnam',      to: '/cosmetic-surgery-vietnam' },
              { label: 'Eye Care in Vietnam',              to: '/eye-care-vietnam' },
              { label: 'Health Check-ups in Vietnam',      to: '/health-checkup-vietnam' },
              { label: 'Urgent Medical Care in Vietnam',   to: '/urgent-medical-care' },
              { label: 'Doctor-to-Hotel in Vietnam',       to: '/doctor-to-hotel-vietnam' },
            ].map((item) => (
              <Link
                key={item.to}
                to={item.to}
                className="inline-block text-xs text-gray-400 hover:text-[#1FA7A1] bg-gray-800 hover:bg-gray-750 border border-gray-700 hover:border-[#1FA7A1]/40 px-3.5 py-2 rounded-full transition-all"
                data-testid={`footer-seo-link-${item.to.replace('/', '')}`}
              >
                {item.label}
              </Link>
            ))}
          </div>
        </div>

        {/* Contact Bar */}
        <div className="border-t border-gray-800 mt-8 pt-8">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex flex-wrap items-center justify-center gap-6 text-sm">
              {contactInfo?.officeAddress && (
                <div className="flex items-center gap-2" data-testid="footer-office-address">
                  <MapPin className="h-4 w-4 text-[#1FA7A1]" />
                  <span>{contactInfo.officeAddress}</span>
                </div>
              )}
              {telHref && contactInfo?.hotlineDisplay && (
                <div className="flex items-center gap-2">
                  <Phone className="h-4 w-4 text-[#1FA7A1]" />
                  <a
                    href={telHref}
                    className="hover:text-[#1FA7A1] transition-colors"
                    data-testid="footer-phone-link"
                  >
                    {contactInfo.hotlineDisplay}
                  </a>
                </div>
              )}
              {contactInfo?.contactEmail && (
                <div className="flex items-center gap-2">
                  <Mail className="h-4 w-4 text-[#1FA7A1]" />
                  <a
                    href={`mailto:${contactInfo.contactEmail}`}
                    className="hover:text-[#1FA7A1] transition-colors"
                    data-testid="footer-email-link"
                  >
                    {contactInfo.contactEmail}
                  </a>
                </div>
              )}
            </div>
            {whatsappHref && (
              <button
                onClick={handleWhatsApp}
                className="flex items-center gap-2 bg-[#25D366] text-white px-4 py-2 rounded-full text-sm font-medium hover:bg-[#128C7E] transition-colors"
                data-testid="footer-whatsapp-btn"
              >
                <MessageCircle className="h-4 w-4" />
                Chat on WhatsApp
              </button>
            )}
          </div>
        </div>

        {/* Healthcare Coordination intro */}
        <div className="border-t border-gray-800 mt-8 pt-6" data-testid="footer-coordination-intro">
          <h3 className="text-sm font-semibold text-white mb-2">Healthcare Coordination in Vietnam</h3>
          <p className="text-xs text-gray-400 leading-relaxed max-w-4xl">
            VietClinics is an independent healthcare coordination service in Vietnam for international patients and travellers. We help people explore dental treatment, cosmetic surgery, hair transplantation, eye care and health check-ups through selected clinic partners. Our English-speaking team can help compare options and coordinate appointments, interpretation and travel support. Urgent-care and doctor-to-hotel requests depend on your location and provider availability.
          </p>
        </div>

        {/* Platform disclaimer */}
        <div className="border-t border-gray-800 mt-6 pt-6">
          <p className="text-xs text-gray-400 leading-relaxed max-w-4xl" data-testid="footer-platform-disclaimer">
            <strong className="text-gray-300">VietClinics is not a hospital or medical provider.</strong>{' '}
            We help patients connect with selected clinic partners. Final diagnosis, medical advice, treatment plans and pricing are provided directly by licensed clinics or doctors.
          </p>
        </div>

        {/* Company details */}
        <div className="border-t border-gray-800 mt-6 pt-6" data-testid="footer-company-details">
          <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-3">
            <div className="text-xs text-gray-400 leading-relaxed max-w-4xl">
              <p className="text-gray-300 font-semibold">{COMPANY_STATEMENT}</p>
              <p className="mt-1.5 flex flex-wrap gap-x-2 gap-y-0.5">
                <span>Company Registration No.: <span className="text-gray-300">{COMPANY_DETAILS.registrationNo}</span></span>
                <span aria-hidden className="hidden sm:inline text-gray-600">|</span>
                <span>Registered office: <span className="text-gray-300">{COMPANY_DETAILS.registeredOffice}</span></span>
                <span aria-hidden className="hidden sm:inline text-gray-600">|</span>
                <span>Vietnam service coordination: <span className="text-gray-300">{COMPANY_DETAILS.vietnamOperations}</span></span>
              </p>
            </div>
            <Link to="/company-details" className="text-xs font-semibold text-[#1FA7A1] hover:text-white transition-colors whitespace-nowrap" data-testid="footer-company-details-link">
              Company Details →
            </Link>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="border-t border-gray-800 mt-6 pt-6 flex flex-col md:flex-row items-center justify-between gap-2 text-sm">
          <p>&copy; 2026 VietClinics. All rights reserved.</p>
          <div className="flex gap-4">
            <Link to="/terms" className="hover:text-[#1FA7A1] transition-colors">Terms</Link>
            <Link to="/privacy" className="hover:text-[#1FA7A1] transition-colors">Privacy</Link>
            <Link to="/contact" className="hover:text-[#1FA7A1] transition-colors">Support</Link>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
